
  import type { PropType } from 'vue';
  import { defineComponent, markRaw } from 'vue';
  import { use } from 'echarts/core';

  import type { TChartWithZoom } from './chartZoomToolbox';
  import EChartsChartZoomToolbar from './EChartsChartZoomToolbar.vue';
  import type { TEChartsChartZoomInstanceApi } from './useEChartsChartZoom';
  import { ECHARTS_CHART_ZOOM_USE, useEChartsChartZoom } from './useEChartsChartZoom';

  use([...ECHARTS_CHART_ZOOM_USE]);

  type TXAxisIndex = number | number[];

  type TVChartLike = {
    chart?: TChartWithZoom | { value?: TChartWithZoom | null };
  };

  const isChartWithZoom = (value: unknown): value is TChartWithZoom =>
    typeof value === 'object' &&
    value !== null &&
    'dispatchAction' in value &&
    typeof value.dispatchAction === 'function';

  const resolveChartInstance = (vChart: TVChartLike | null | undefined): TChartWithZoom | null => {
    if (!vChart?.chart) return null;

    const chartRef = vChart.chart;
    const chart = 'value' in chartRef ? chartRef.value : chartRef;
    return isChartWithZoom(chart) ? chart : null;
  };

  export default defineComponent({
    name: 'EChartsChartZoom',
    components: {
      EChartsChartZoomToolbar,
    },
    props: {
      enabled: {
        type: Boolean,
        default: false,
      },
      xAxisIndex: {
        type: [Number, Array] as PropType<TXAxisIndex>,
        default: 0,
      },
    },
    data(): { zoomApi: TEChartsChartZoomInstanceApi } {
      return {
        zoomApi: markRaw(useEChartsChartZoom()),
      };
    },
    methods: {
      withZoom<T extends Record<string, unknown>>(option: T): T {
        if (!this.enabled) return option;
        return { ...option, ...this.zoomApi.getZoomOption(this.xAxisIndex) };
      },
      bindVChart(vChart: TVChartLike | null | undefined): void {
        if (!this.enabled) return;
        const chart = resolveChartInstance(vChart);
        if (!chart) return;
        this.zoomApi.bindChart(chart);
      },
    },
  });
